import { createSlice } from '@reduxjs/toolkit';

// Filter operators:
// 'equals' (default) - exact match
// 'contains' - value contains the filter value (case-insensitive)
// 'startsWith' - value starts with the filter value
// 'endsWith' - value ends with the filter value

// Surface buttons configuration with tab mappings
// IDs must match keys in SURFACE_CONFIG below
export const SURFACE_BUTTONS_CONFIG = {
    exterior: [
        { id: 'Front Door', label: 'Front Door' },
        { id: 'Roof', label: 'Roof' },
        { id: 'Stone/Brick', label: 'Stone/Brick' },
        // { id: 'Tile', label: 'Tile' },
        // { id: 'Body', label: 'Body' },
        // { id: 'Trim', label: 'Trim' },
    ],
    kitchen: [
        { id: 'Floor', label: 'Floor' },
        { id: 'Backsplash', label: 'Backsplash' },
        { id: 'Counters', label: 'Counters' },
        { id: 'Cabinet Doors', label: 'Cabinet Doors' },
        { id: 'Painted Walls', label: 'Painted Walls' },
        { id: 'Trim', label: 'Trim' },
    ]
};

// Surface configuration with series ID, hardcoded filters, and dropdown columns
// Filter format: { column: 'columnName', value: 'filterValue', operator: 'equals|contains|startsWith|endsWith' }
// dropdownColumn: column name to use for the first DropdownFilter in ProductMenu
// dropdownColumnTwo: column name to use for the second DropdownFilter in ProductMenu
export const SURFACE_CONFIG = {
    // Exterior surfaces
    'Roof': {
        seriesId: 9765,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    },
    'Body': {
        seriesId: 9762,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    },
    'Front Door': {
        seriesId: 9699,
        filters: [],
        dropdownColumn: '',
        dropdownColumnTwo: ''
    },
    'Trim': {
        seriesId: 9764,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    },
    // Kitchen surfaces
    'Floor': {
        seriesId: 9760,
        filters: [],
        dropdownColumn: 'ecoFriendly',
        dropdownColumnTwo: 'name'
    },
    'Backsplash': {
        seriesId: 9757,
        filters: [
            { column: 'defaultRoomSceneSpecifier', value: 'Backsplash', operator: 'contains' }
        ],
        dropdownColumn: 'name',
        dropdownColumnTwo: 'ecoFriendly'
    },
    'Counters': {
        seriesId: 9758,
        filters: [],
        dropdownColumn: 'ecoFriendly',
        dropdownColumnTwo: 'ecoFriendly'
    },
    'Cabinet Doors': {
        seriesId: 9761,
        filters: [
            { column: 'categoryName', value: 'Cabinet Doors', operator: 'equals' }
        ],
        dropdownColumn: 'ecoFriendly',
        dropdownColumnTwo: 'color'
    },
    'Painted Walls': {
        seriesId: 9764,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    },
    // Exterior stone & tile surfaces
    'Stone/Brick': {
        seriesIds: [9763, 9762],  // Stone: 9763, Brick: 9762
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color',
        groupByColumn: 'categoryName',
        groupByColumnOrder: ['Stone', 'Brick']
    },
    'Stone': {
        seriesId: 9763,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    },
    'Brick': {
        seriesId: 9762,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    },
    'Tile': {
        seriesId: 9757,
        filters: [],
        dropdownColumn: 'warranty',
        dropdownColumnTwo: 'color'
    }
};

// Helper to get series ID from surface name (returns single ID or null)
export const getSeriesIdForSurface = (surfaceName) => {
    return SURFACE_CONFIG[surfaceName]?.seriesId || null;
};

// Helper to get series IDs from surface name (returns array, supports both single and multi)
export const getSeriesIdsForSurface = (surfaceName) => {
    const config = SURFACE_CONFIG[surfaceName];
    if (!config) return [];
    if (config.seriesIds) return config.seriesIds;
    if (config.seriesId) return [config.seriesId];
    return [];
};

// Helper to get hardcoded filters for a surface
export const getFiltersForSurface = (surfaceName) => {
    return SURFACE_CONFIG[surfaceName]?.filters || [];
};

// Helper to get dropdown column for a surface
export const getDropdownColumnForSurface = (surfaceName) => {
    return SURFACE_CONFIG[surfaceName]?.dropdownColumn || 'warranty';
};

// Helper to get second dropdown column for a surface
export const getDropdownColumnTwoForSurface = (surfaceName) => {
    return SURFACE_CONFIG[surfaceName]?.dropdownColumnTwo || 'color';
};

/**
 * Apply a single filter to check if a product matches
 * @param {Object} product - The product to check
 * @param {Object} filter - Filter with column, value, and optional operator
 * @returns {boolean} - Whether the product matches the filter
 */
export const applyFilter = (product, filter) => {
    const { column, value, operator = 'equals' } = filter;
    const productValue = product[column];
    
    if (productValue === null || productValue === undefined) {
        return false;
    }
    
    const productValueStr = String(productValue).toLowerCase();
    const filterValueStr = String(value).toLowerCase();
    
    switch (operator) {
        case 'contains':
            return productValueStr.includes(filterValueStr);
        case 'startsWith':
            return productValueStr.startsWith(filterValueStr);
        case 'endsWith':
            return productValueStr.endsWith(filterValueStr);
        case 'equals':
        default:
            return productValueStr === filterValueStr;
    }
};

/**
 * Apply multiple filters to check if a product matches all of them
 * @param {Object} product - The product to check
 * @param {Array} filters - Array of filter objects
 * @returns {boolean} - Whether the product matches all filters
 */
export const applyFilters = (product, filters) => {
    if (!filters || filters.length === 0) return true;
    return filters.every(filter => applyFilter(product, filter));
};

const initialState = {
    // Track the currently selected surface button
    selectedSurface: null,
    // Track selected surfaces per tab (exterior/kitchen may have different selections)
    selectedSurfaces: {
        exterior: null,        // Start with null since Paint Packages is default
        kitchen: 'Floor'       // Default to first button for kitchen
    },
    // Track the last clicked surface button (global, across tabs)
    lastClickedSurfaceButton: null,
    // Active series ID for fetching products based on selected surface
    activeSeriesId: null,      // Start with null since Paint Packages is default
    // Active series IDs array for surfaces with multiple series (e.g., Stone/Brick)
    activeSeriesIds: null,
    // Active hardcoded filters for the selected surface
    activeHardcodedFilters: [],
    // Active dropdown column for the selected surface
    activeDropdownColumn: 'warranty',
    // Active second dropdown column for the selected surface
    activeDropdownColumnTwo: 'color',
    // Active groupByColumn for the selected surface (e.g., 'categoryName' for Stone/Brick)
    activeGroupByColumn: null,
    // Active groupByColumnOrder for the selected surface
    activeGroupByColumnOrder: [],
    // Track if Paint Packages view is active (to prevent surface auto-selection)
    paintPackagesActive: true, // Start with Paint Packages active
    // Selected paint package from PaintPackages component
    selectedPaintPackage: null,
    // Track applied products per tab (keyed by surface)
    exteriorAppliedProducts: {},
    kitchenAppliedProducts: {},
    // Selected interior floor plan (matching the selected exterior)
    selectedInteriorFloorPlan: null,
    // Floor plan info for footer and print (name, sqft, beds, bath, garageCount)
    floorPlanInfo: null,
    // Image URL for the selected floor plan thumbnail
    selectedFloorPlanImageUrl: null,
    // Visualizer data for each tab
    vizDataByTab: {
        exterior: {
            specifier: 'Avencia\\Images\\Genesee_Exterior',
            sel: '-1'
        },
        kitchen: {
            specifier: 'Avencia\\Images\\Byford_Kitchen',
            sel: '-1'
        }
    },
    // Surfaces detected in the current scene (used to show/hide conditional buttons like Stone)
    sceneSurfaces: []
};

const featuresSlice = createSlice({
    name: "features",
    initialState,
    reducers: {
        // Set the selected surface (generic)
        setSelectedSurface(state, action) {
            state.selectedSurface = action.payload;
        },
        // Set selected surface for a specific tab and update activeSeriesId
        setSelectedSurfaceForTab(state, action) {
            const { tab, surfaceId } = action.payload;
            state.selectedSurfaces[tab] = surfaceId;
            // Track the last clicked button globally
            state.lastClickedSurfaceButton = { tab, surfaceId };
            // Update activeSeriesId/activeSeriesIds, hardcoded filters, and dropdown columns based on surface config
            const config = SURFACE_CONFIG[surfaceId];
            // Support both single seriesId and array seriesIds
            if (config?.seriesIds) {
                state.activeSeriesId = null;
                state.activeSeriesIds = config.seriesIds;
            } else {
                state.activeSeriesId = config?.seriesId || null;
                state.activeSeriesIds = null;
            }
            state.activeHardcodedFilters = config?.filters || [];
            state.activeDropdownColumn = config?.dropdownColumn || 'warranty';
            state.activeDropdownColumnTwo = config?.dropdownColumnTwo || 'color';
            state.activeGroupByColumn = config?.groupByColumn || null;
            state.activeGroupByColumnOrder = config?.groupByColumnOrder || [];
            // Deactivate paint packages when a surface is selected
            state.paintPackagesActive = false;
        },
        // Set active series ID directly
        setActiveSeriesId(state, action) {
            state.activeSeriesId = action.payload;
        },
        // Clear selected surface
        clearSelectedSurface(state) {
            state.selectedSurface = null;
        },
        // Clear selected surface for a specific tab
        clearSelectedSurfaceForTab(state, action) {
            const tab = action.payload;
            state.selectedSurfaces[tab] = null;
            state.activeSeriesId = null;
            state.paintPackagesActive = true;
        },
        // Sync activeSeriesId when switching tabs (uses the selected surface for that tab)
        syncActiveSeriesForTab(state, action) {
            const tab = action.payload;
            const surfaceId = state.selectedSurfaces[tab];
            const config = SURFACE_CONFIG[surfaceId];
            // Support both single seriesId and array seriesIds
            if (config?.seriesIds) {
                state.activeSeriesId = null;
                state.activeSeriesIds = config.seriesIds;
            } else {
                state.activeSeriesId = config?.seriesId || null;
                state.activeSeriesIds = null;
            }
            state.activeHardcodedFilters = config?.filters || [];
            state.activeDropdownColumn = config?.dropdownColumn || 'warranty';
            state.activeDropdownColumnTwo = config?.dropdownColumnTwo || 'color';
            state.activeGroupByColumn = config?.groupByColumn || null;
            state.activeGroupByColumnOrder = config?.groupByColumnOrder || [];
            // For exterior tab, if no surface is selected, paint packages should be active
            // For other tabs, paint packages is not applicable
            if (tab === 'exterior') {
                state.paintPackagesActive = surfaceId === null;
            } else {
                state.paintPackagesActive = false;
            }
        },
        // Clear all selected surfaces
        clearAllSelectedSurfaces(state) {
            state.selectedSurface = null;
            state.selectedSurfaces = {
                exterior: null,
                kitchen: null
            };
            state.lastClickedSurfaceButton = null;
            // Reset to no active series when clearing all
            state.activeSeriesId = null;
            state.activeSeriesIds = null;
            state.activeHardcodedFilters = [];
            state.activeDropdownColumn = 'warranty';
            state.activeDropdownColumnTwo = 'color';
            state.activeGroupByColumn = null;
            state.activeGroupByColumnOrder = [];
            state.paintPackagesActive = true;
        },
        // Set selected paint package
        setSelectedPaintPackage(state, action) {
            state.selectedPaintPackage = action.payload;
        },
        // Clear selected paint package
        clearSelectedPaintPackage(state) {
            state.selectedPaintPackage = null;
        },
        // Set applied product for a tab and surface
        setAppliedProduct(state, action) {
            const { tab, surface, product } = action.payload;
            if (tab === 'exterior') {
                state.exteriorAppliedProducts[surface] = product;
            } else if (tab === 'kitchen') {
                state.kitchenAppliedProducts[surface] = product;
            }
        },
        // Clear applied product for a tab and surface
        clearAppliedProduct(state, action) {
            const { tab, surface } = action.payload;
            if (tab === 'exterior') {
                delete state.exteriorAppliedProducts[surface];
            } else if (tab === 'kitchen') {
                delete state.kitchenAppliedProducts[surface];
            }
        },
        // Clear all applied products for a tab
        clearAllAppliedProductsForTab(state, action) {
            const tab = action.payload;
            if (tab === 'exterior') {
                state.exteriorAppliedProducts = {};
            } else if (tab === 'kitchen') {
                state.kitchenAppliedProducts = {};
            }
        },
        // Set selected interior floor plan
        setSelectedInteriorFloorPlan(state, action) {
            state.selectedInteriorFloorPlan = action.payload;
        },
        // Clear selected interior floor plan
        clearSelectedInteriorFloorPlan(state) {
            state.selectedInteriorFloorPlan = null;
        },
        // Set visualizer data for a specific tab
        setVizDataForTab(state, action) {
            const { tab, specifier, sel } = action.payload;
            state.vizDataByTab[tab] = { specifier, sel };
        },
        // Set floor plan info for footer and print
        setFloorPlanInfo(state, action) {
            state.floorPlanInfo = action.payload;
        },
        // Set selected floor plan image URL
        setSelectedFloorPlanImageUrl(state, action) {
            state.selectedFloorPlanImageUrl = action.payload;
        },
        // Set detected surfaces from the current scene
        setSceneSurfaces(state, action) {
            state.sceneSurfaces = action.payload;
        }
    }
});

export const featuresActions = featuresSlice.actions;
export default featuresSlice.reducer;
